import Comparison from '@/types/comparison';
import User from '@/types/user';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';
import { isFFEnabled } from '@/utils/feature-flag';
import { FEATURE_FLAGS } from '@/enums/FeatureFlags';
import { PortfolioResolutionStrategy } from './portfolio-resolution-strategy';
import { CategoryBasedStrategy } from './category-based-strategy';
import { AccountSetStrategy } from './account-set-strategy';

/**
 * Checks if account sets feature is enabled and comparison has valid account sets
 *
 * Returns true only if ALL of the following conditions are met:
 * 1. User has COMPARISON_ACCOUNT_SETS permission
 * 2. ACCOUNT_SETS_ENABLED feature flag is enabled
 * 3. Comparison has account sets defined
 * 4. At least one account set has a portfolio assigned (not all -1)
 *
 * This ensures we only use AccountSetStrategy when account sets are actually
 * configured and usable, preventing errors from empty/invalid account sets.
 */
const shouldUseAccountSets = (user: User, comparison: Comparison): boolean => {
  return (
    userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_ACCOUNT_SETS) &&
    isFFEnabled(FEATURE_FLAGS.ACCOUNT_SETS_ENABLED) &&
    !!comparison.accountSets &&
    comparison.accountSets.length > 0 &&
    comparison.accountSets.some(set => set.current !== -1 || set.recommendations.some(rec => rec.itemId !== -1))
  );
};

/**
 * Factory function to get the appropriate portfolio resolution strategy
 *
 * This is the single entry point for obtaining a strategy instance.
 * It encapsulates the decision logic for which strategy to use, keeping
 * that complexity out of the calling code.
 *
 * Why use a factory?
 * ==================
 *
 * 1. SINGLE DECISION POINT
 *    - Strategy selection logic exists in exactly one place
 *    - Easy to modify selection criteria without touching callers
 *    - Reduces duplication of complex selection logic
 *
 * 2. ABSTRACTION
 *    - Callers don't need to know about concrete strategy classes
 *    - Callers don't need to know selection criteria
 *    - Can change strategy classes without changing callers
 *
 * 3. CONSISTENCY
 *    - All code uses same logic to select strategy
 *    - Prevents bugs from inconsistent selection
 *    - Easier to reason about system behavior
 *
 * 4. TESTABILITY
 *    - Can mock this factory in tests
 *    - Can inject specific strategies for testing
 *    - Easier to test validation logic in isolation
 *
 * Example Usage:
 * ==============
 * ```typescript
 * // In validation code:
 * const strategy = getPortfolioResolutionStrategy(user, comparison);
 * const recommended = strategy.getRecommendedPortfolios(comparison);
 *
 * // That's it! No need to worry about:
 * // - Which strategy to use
 * // - How to instantiate it
 * // - What conditions determine the choice
 * ```
 *
 * Migration Path:
 * ===============
 * During overlap period:
 * - Returns AccountSetStrategy when feature is enabled
 * - Returns CategoryBasedStrategy otherwise
 *
 * After deprecation:
 * - Simply change return to always be AccountSetStrategy
 * - No changes needed anywhere else in the codebase
 *
 * @param user - User object to check permissions
 * @param comparison - Comparison object to check for account sets
 * @returns The appropriate strategy (AccountSetStrategy or CategoryBasedStrategy)
 */
export const getPortfolioResolutionStrategy = (user: User, comparison: Comparison): PortfolioResolutionStrategy => {
  return shouldUseAccountSets(user, comparison) ? new AccountSetStrategy() : new CategoryBasedStrategy();
};
